<script setup>
// The UNEXPECTED double-book: Bizzabo holds a registration for a session that
// isn't on the attendee's schedule, so adding or seating something in that slot
// is refused because of a session they cannot see anywhere (Jack, 2026-08-20).
//
// Distinct from double-book.vue, which covers a clash with a session they CAN
// see — that one only needs to point at it. Here the useful thing is a choice,
// and the copy has to explain why a session they never added is in the way.
//
// Design: Nick, 2026-08-20. The PRIMARY action keeps the session Bizzabo already
// holds; adding the requested one instead is the secondary. That ordering is
// deliberate — the registration that exists is the one the attendee actually
// made, wherever they made it.
//
// Resolves through emit('close', value), the same contract the other dialogs
// use: the opener awaits the value and performs the action, so this stays
// presentational.
const props = defineProps({
    // The session the attendee was trying to add or reserve.
    sessionTitle: { type: String, default: '' },
    // The clashing one, straight from the endpoint's `clash` payload.
    clash: { type: Object, required: true },
});

const open = defineModel('open', { default: true });
const emit = defineEmits(['close']);

import { modal_button_outline_2026 } from '~~/shared/utilities/ui';

const result = ref(null);

const otherTitle = computed(() => props.clash?.title || 'another session');
const clashId = computed(() => props.clash?.unique_id || null);

// Button labels carry both titles, and a session title can run to a full line of
// serif copy — so they are clipped to a recognisable stem rather than allowed to
// wrap the button into a paragraph (Nick's comp shows both truncated).
const stem = (t, max = 26) => {
    const s = String(t || '').trim();
    if (s.length <= max) return s;
    const cut = s.slice(0, max);
    const space = cut.lastIndexOf(' ');
    return `${(space > 12 ? cut.slice(0, space) : cut).trimEnd()}…`;
};

const choose = (value) => {
    result.value = value;
    open.value = false;
};

// Same conceal-don't-close mechanism double-book.vue uses. Closing this and then
// navigating raced the drawer's setup and left the page inert once the drawer was
// dismissed; concealing keeps the modal mounted so there is nothing to race.
// Conceal only when the drawer becomes something OTHER than whatever was already
// showing when this modal opened.
//
// It used to be a bare `!!route.query.drawer`, which reads "a drawer is up, so
// hide". That is right when the attendee clicks through from here to a session —
// but wrong when the modal is opened FROM a page that already has a drawer open
// (any `?drawer=` URL, which is how the session tray deep-links). Then it was
// concealed from birth and never appeared at all: the progress screen flashed and
// the attendee was left with nothing (Jack, 2026-08-20).
const route = useRoute();
const drawerAtOpen = route.query.drawer ?? null;
const concealed = computed(() => {
    const now = route.query.drawer ?? null;
    return !!now && now !== drawerAtOpen;
});
const openClash = async () => {
    if (!clashId.value) return;
    // The global modal middleware rewrites /sessions/{id} into `?drawer=`, which
    // app.vue renders over this modal.
    await navigateTo(`/sessions/${clashId.value}`);
};

// Fires once when the shell goes open -> closed by any route. Anything other
// than an explicit choice resolves to null, which the opener treats as "leave
// everything as it was".
const onShellClose = () => emit('close', result.value);
</script>

<template>
    <FragmentsModalShell
        v-model:open="open"
        headline="Looks like you’ve already booked something during this time slot!"
        :concealed="concealed"
        @close="onShellClose"
    >
        <div class="flex flex-col items-center gap-[30px] w-full">
            <!-- White notice box: alert icon + the explanation, naming the session
                 that is in the way and linking through to it. -->
            <div class="flex items-start gap-[14px] w-full text-left bg-white rounded-[10px] px-[28px] py-[25px]">
                <svg class="shrink-0 mt-[4px] size-[16px] text-(--magenta)" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <circle cx="8" cy="8" r="8" fill="currentColor" />
                    <path d="M8 4V9" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                    <circle cx="8" cy="11.6" r="0.9" fill="white" />
                </svg>
                <p class="font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] text-(--magenta) m-0">
                    “<button
                        type="button"
                        data-test="double-book-clash-link"
                        class="bg-transparent border-none p-0 cursor-pointer font-inherit text-(--magenta)
                            underline decoration-solid underline-offset-[3px] hover:no-underline"
                        @click="openClash"
                    >{{ otherTitle }}</button>” was added to My Schedule using the UNBOUND app, but has not
                    synced with the UNBOUND website yet. You can only add a single session to My Schedule
                    during this time slot.
                </p>
            </div>

            <div class="flex flex-col items-center gap-[16px] w-full">
                <!-- Primary: keep what Bizzabo already holds. -->
                <button
                    type="button"
                    data-test="double-book-keep-other"
                    class="inline-flex items-center justify-center rounded-[50px] px-[28px] py-[12px]
                        bg-(--magenta) border border-(--magenta) text-white
                        font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] cursor-pointer
                        transition-colors duration-200 hover:bg-(--orange) hover:border-(--orange)
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta)"
                    @click="choose('keep-other')"
                >Keep “{{ stem(otherTitle) }}” on My Schedule</button>

                <!-- Secondary: take the one they just asked for instead. -->
                <button
                    type="button"
                    data-test="double-book-take-this"
                    :class="modal_button_outline_2026"
                    @click="choose('take-this')"
                >Add “{{ stem(sessionTitle) }}” to My Schedule Instead</button>
            </div>
        </div>

        <template #actions>
            <button
                type="button"
                data-test="double-book-cancel"
                class="bg-transparent border-none p-0 cursor-pointer
                    font-hubspot-sans font-light text-[14px] leading-[1.5] tracking-[0.28px]
                    text-(--darker-pink) underline decoration-dotted decoration-[8.5%] underline-offset-[3px]
                    transition-colors duration-200 hover:text-(--purple) hover:no-underline"
                @click="open = false"
            >Cancel</button>
        </template>
    </FragmentsModalShell>
</template>
